import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Spinner } from './Spinner';
import { cn } from '@/lib/cn';

const button = cva(
  'inline-flex min-h-11 select-none whitespace-nowrap items-center justify-center gap-2 rounded-control px-4 text-[15px] font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-fg hover:brightness-110 active:brightness-95',
        secondary: 'border border-line bg-surface-2 text-fg hover:bg-line/40',
        ghost: 'text-fg hover:bg-surface-2',
        danger: 'bg-negative text-negative-fg hover:brightness-110',
      },
      size: { md: '', lg: 'min-h-12 px-5 text-base', icon: 'size-11 px-0' },
      block: { true: 'w-full', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', block: false },
  },
);

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {
  loading?: boolean;
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, block, loading, asChild, children, disabled, type = 'button', ...rest },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      className={cn(button({ variant, size, block }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {asChild ? children : (<>{loading && <Spinner />}{children}</>)}
    </Comp>
  );
});
